import { useState, useCallback, useEffect, type RefObject } from 'react'
import { SCROLL_CHECK_DELAY_MS } from '@/constants/timing'

const SCROLL_THRESHOLD = 10

interface UseTableScrollOptions {
  scrollContainerRef: RefObject<HTMLDivElement | null>
  hasMore?: boolean
  isLoadingMore?: boolean
  onLoadMore?: () => void
}

interface UseTableScrollReturn {
  /** Whether the shadow should be visible (content is scrollable and not at bottom) */
  shouldShowShadow: boolean
  /** Handle scroll events - call this from onScroll */
  handleScroll: (e: React.UIEvent<HTMLDivElement>) => void
}

export function useTableScroll({
  scrollContainerRef,
  hasMore = false,
  isLoadingMore = false,
  onLoadMore,
}: UseTableScrollOptions): UseTableScrollReturn {
  const [isScrollable, setIsScrollable] = useState(false)
  const [isNearBottom, setIsNearBottom] = useState(false)

  const checkScrollState = useCallback(() => {
    if (scrollContainerRef.current) {
      const { scrollHeight, clientHeight, scrollTop } =
        scrollContainerRef.current
      const isContentScrollable = scrollHeight > clientHeight
      const isAtBottom =
        scrollHeight - scrollTop - clientHeight < SCROLL_THRESHOLD

      setIsScrollable(isContentScrollable)
      setIsNearBottom(isAtBottom)
    }
  }, [scrollContainerRef])

  // Check scroll state on mount and when content changes
  useEffect(() => {
    checkScrollState()

    // Also check after a short delay to account for layout shifts
    const timeoutId = setTimeout(checkScrollState, SCROLL_CHECK_DELAY_MS)
    return () => clearTimeout(timeoutId)
  }, [checkScrollState])

  // Observe resize changes
  useEffect(() => {
    const container = scrollContainerRef.current
    if (!container) return

    const resizeObserver = new ResizeObserver(() => {
      checkScrollState()
    })

    resizeObserver.observe(container)
    return () => resizeObserver.disconnect()
  }, [scrollContainerRef, checkScrollState])

  const handleScroll = useCallback(
    (e: React.UIEvent<HTMLDivElement>) => {
      const target = e.currentTarget
      const { scrollHeight, scrollTop, clientHeight } = target

      const isContentScrollable = scrollHeight > clientHeight
      const isAtBottom =
        scrollHeight - scrollTop - clientHeight < SCROLL_THRESHOLD

      setIsScrollable(isContentScrollable)
      setIsNearBottom(isAtBottom)

      // Trigger load more when near bottom
      if (hasMore && !isLoadingMore && onLoadMore) {
        const scrollBottom = scrollHeight - scrollTop - clientHeight
        if (scrollBottom < 200) {
          onLoadMore()
        }
      }
    },
    [hasMore, isLoadingMore, onLoadMore]
  )

  // Shadow should be visible when content is scrollable and not near bottom
  const shouldShowShadow = isScrollable && !isNearBottom

  return {
    shouldShowShadow,
    handleScroll,
  }
}
